'use client';

import { OptimizedImage } from '@/components/optimized-image';
import Link from 'next/link';
import { formatRelativeTime, formatCount } from '@/lib/utils';
import { Grid2x2Plus, ExternalLink, Share2, Heart, Cpu, Repeat2, Eye, EyeOff, Bell, BellPlus, BellMinus, MoreHorizontal, Pin, Link2, Check, User, Trash2, Bookmark, Quote, Flag, Pencil, UserPlus } from 'lucide-react';
import { ChatIcon } from '@/components/icons/chat-icon';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { cn } from '@/lib/utils';
import { RichContent } from '@/lib/content-parser';
import { detectPlatformEmbed, extractFirstEmbed } from '@/lib/platform-registry';
import { PlatformEmbed, RichLinkCard, type LinkMeta } from './platform-embed';
import { PostLightbox } from './post-lightbox';
import { FlagReportModal } from './flag-report-modal';
import { PostMediaGallery } from './post-media-gallery';
import { normalizePostMedia, type PostMediaItem } from '@/lib/posts/media';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from './auth-provider';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { tracker } from '@/lib/tracking';
import { haptic } from '@/lib/haptic';
import { useTranslation } from './i18n-provider';
import { Avatar } from '@/components/avatar';
import { Tip } from '@/components/ui/tooltip';

interface Post {
  id: string;
  title?: string | null;
  content?: string | null;
  mediaType?: string | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  mediaWidth?: number;
  mediaHeight?: number;
  /** Multi-item gallery (normalized by /api/posts). Falls back to the single
   *  mediaUrl/mediaType columns when absent. */
  media?: PostMediaItem[];
  linkUrl?: string | null;
  linkMeta?: LinkMeta | null;
  chatCta?: string | null;
  /**
   * Set when the post advertises a widget — the underlying content id
   * the widget marketplace publishes against. Inside the workspace
   * canvas this turns the bottom-right chat CTA into an "Add widget"
   * action that drops the widget onto the board. Optional; absent on
   * regular posts and on every non-workspace surface.
   */
  widgetContentId?: string | null;
  /**
   * Composer-attached agent promo (posts.metadata.attachedAgentId) — the
   * card renders a Chat / Add-agent CTA for THIS agent even when the post
   * author is a human. Set alongside attachedAgentUsername by /api/posts.
   */
  attachedAgentId?: string | null;
  attachedAgentUsername?: string | null;
  /**
   * Plan v3-05 attribution — present when the post's widget content row
   * carries a non-null `remix_of_post_id`. Server-side helper
   * `lib/posts/enrich-remix.ts` fills this in via a single LEFT JOIN so
   * the card renders "Remixed from @bob/widget-x" without an extra
   * round-trip. Read-only label, no CTA (anti-feature-creep guard:
   * tests forbid any interactive affordance in this attribution row).
   */
  remixOfPost?: {
    id: string;
    title?: string | null;
    authorUsername?: string | null;
    postUrl: string;
  } | null;
  humanLikes: number;
  agentLikes: number;
  humanDislikes: number;
  agentDislikes: number;
  commentCount: number;
  viewCount?: number;
  repostCount?: number;
  totalLikes: number;
  totalDislikes: number;
  userVotes: string[];
  isNsfw?: boolean;
  isPinned?: boolean;
  isFollowing?: boolean;
  isReposted?: boolean;
  isSaved?: boolean;
  agentEndorsed?: boolean;
  isProfilePinned?: boolean;
  quotedPost?: {
    id: string;
    title?: string | null;
    content?: string | null;
    mediaType?: string | null;
    mediaUrl?: string | null;
    thumbnailUrl?: string | null;
    createdAt: string;
    author?: {
      id: string;
      username: string;
      displayName: string;
      avatarUrl: string | null;
      accountType: 'human' | 'agent';
    } | null;
    deleted?: boolean;
  } | null;
  createdAt: string;
  author: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    accountType: 'human' | 'agent';
    reputationScore: string;
    ownerId?: string | null;
  };
  channel?: {
    id: string;
    name: string;
    slug: string;
    ownerId?: string | null;
  } | null;
  tags?: { id: string; name: string; color?: string | null }[];
}

export type { Post as PostData };

interface PostCardProps {
  post: Post;
  compact?: boolean;
  expanded?: boolean;
  /** Hint to prioritize loading of media (above-the-fold) */
  priority?: boolean;
  /** Media has been prefetched into browser cache by the preloader */
  preloaded?: boolean;
  /** Ref callback for the preloader's IntersectionObserver */
  cardRef?: (el: HTMLElement | null) => void;
  /**
   * Optional callback that, when present, replaces the four
   * `Link href="/p/[id]"` affordances on the card (title, body,
   * comment row, quoted post) with onClick handlers that call this
   * callback with the target post id. The workspace Zap panel uses
   * this to keep post drill-downs inside the panel's navigation
   * stack instead of routing to the standalone /p/[id] page.
   */
  onOpenPost?: (postId: string) => void;
  /**
   * When present, the "Edit" action calls this instead of navigating
   * to `/p/[id]/edit`. The in-panel detail view uses it to switch the
   * card into an inline edit form — no page reload, no fullscreen
   * dialog. Absent everywhere else, so the standalone edit route is
   * unchanged.
   */
  onEdit?: (postId: string) => void;
  /**
   * Called after the author deletes this post. The in-panel detail
   * view passes its Back handler so a delete pops the user back to the
   * feed instead of leaving an empty detail view behind.
   */
  onDeleted?: () => void;
}

const CONTENT_LINE_CLAMP = 6;
const CONTENT_CHAR_LIMIT = 280;

export function PostCard({ post, compact = false, expanded = false, priority, preloaded, cardRef, onOpenPost, onEdit, onDeleted }: PostCardProps) {
  // When onOpenPost is provided, post-detail navigation is captured
  // by the host instead of leaving the page via a `<Link>`. We render
  // an interactive `<button>` styled to match the original anchor so
  // hover states stay identical.
  const PostNav = ({
    targetId,
    className,
    children,
    onMouseDown,
  }: {
    targetId: string;
    className?: string;
    children: React.ReactNode;
    onMouseDown?: (e: React.MouseEvent) => void;
  }) => {
    if (onOpenPost) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenPost(targetId);
          }}
          onMouseDown={onMouseDown}
          className={cn('text-left', className)}
        >
          {children}
        </button>
      );
    }
    return (
      <Link href={`/p/${targetId}`} onMouseDown={onMouseDown} className={className}>
        {children}
      </Link>
    );
  };

  const { user, nsfwMode } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();
  const chatSidebar = useChatSidebar();
  // `null` everywhere outside the workspace shell — keeps the
  // default chat-sidebar CTA intact on /discover, /marketplace, etc.
  // Inside the workspace, swaps the chat icon for an
  // "Add agent" / "Add widget" action that drops the item onto the
  // canvas instead of opening a separate chat thread.
  const wsCardActions = useWorkspaceCardActions();
  const isAgent = post.author.accountType === 'agent';
  // The agent the trailing CTA targets: a composer-attached agent promo
  // wins; else an agent author promotes itself. Null → no agent CTA.
  const ctaAgent =
    post.attachedAgentId && post.attachedAgentUsername
      ? {
          id: post.attachedAgentId,
          username: post.attachedAgentUsername,
          displayName: post.attachedAgentUsername,
          avatarUrl: null as string | null,
        }
      : isAgent
        ? {
            id: post.author.id,
            username: post.author.username,
            displayName: post.author.displayName,
            avatarUrl: post.author.avatarUrl,
          }
        : null;
  // Author, the author-agent's owner, site staff — or the owner of the
  // channel the post lives in (the API also honours channel moderators via
  // canModerateChannel; grant-based mods just won't see the menu until a
  // perms fetch exists client-side).
  const canManage = !!user && (
    user.id === post.author.id ||
    user.id === post.author.ownerId ||
    user.role === 'admin' ||
    user.role === 'moderator' ||
    (!!post.channel?.ownerId && post.channel.ownerId === user.id)
  );
  const usernamePrefix = '@';
  const postRef = useRef<HTMLElement>(null);
  const combinedRef = useCallback((el: HTMLElement | null) => {
    (postRef as React.MutableRefObject<HTMLElement | null>).current = el;
    cardRef?.(el);
  }, [cardRef]);
  const hasTrackedView = useRef(false);
  
  // Time translations for locale-aware formatting
  const timeTranslations = {
    justNow: t('time.justNow'),
    minutesAgo: t('time.minutesAgo'),
    hoursAgo: t('time.hoursAgo'),
    daysAgo: t('time.daysAgo'),
  };
  
  // Like state
  const [isLiked, setIsLiked] = useState(post.userVotes?.includes('up') || false);
  const [humanLikes, setHumanLikes] = useState(post.humanLikes);
  const [agentLikes, setAgentLikes] = useState(post.agentLikes);
  const [isLiking, setIsLiking] = useState(false);
  
  // Comment count state (synced via commentAdded event)
  const [localCommentCount, setLocalCommentCount] = useState(post.commentCount);

  // Dedup tags by id — the backend join occasionally emits the same tag
  // twice for posts that surface through multiple channel paths.
  const uniqueTags = useMemo(() => {
    const seen = new Set<string>();
    return (post.tags ?? []).filter((tag) => {
      if (seen.has(tag.id)) return false;
      seen.add(tag.id);
      return true;
    });
  }, [post.tags]);

  useEffect(() => {
    const handleCommentAdded = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.postId === post.id) {
        setLocalCommentCount(prev => prev + 1);
      }
    };
    window.addEventListener('commentAdded', handleCommentAdded);
    return () => window.removeEventListener('commentAdded', handleCommentAdded);
  }, [post.id]);

  // Re-sync when the post itself changes. useState only reads its initial
  // value once, so a count that moved for any reason other than the
  // `commentAdded` event above (a deleted comment, a refetch) left the badge
  // showing a stale number next to a correct header.
  useEffect(() => {
    setLocalCommentCount(post.commentCount);
  }, [post.id, post.commentCount]);

  // Follow / Repost / Pin / Save state
  const [isFollowingPost, setIsFollowingPost] = useState(post.isFollowing || false);
  const [isReposted, setIsReposted] = useState(post.isReposted || false);
  const [repostCount, setRepostCount] = useState(post.repostCount || 0);
  const [isPinned, setIsPinned] = useState(post.isPinned || false);
  const [isProfilePinned, setIsProfilePinned] = useState(post.isProfilePinned || false);
  const [isSaved, setIsSaved] = useState(post.isSaved || false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [repostMenuOpen, setRepostMenuOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const repostMenuRef = useRef<HTMLDivElement>(null);
  
  // NSFW reveal state
  const [nsfwRevealed, setNsfwRevealed] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Info dialog state
  const [agentLikesDialogOpen, setAgentLikesDialogOpen] = useState(false);
  const [viewsDialogOpen, setViewsDialogOpen] = useState(false);

  // User menu state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [isFollowing, setIsFollowing] = useState<boolean | null>(null);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    if (!userMenuOpen || !user || isFollowing !== null) return;
    fetch(`/api/users/${post.author.username}/follow`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setIsFollowing(d.following); })
      .catch(() => {});
  }, [userMenuOpen, user, isFollowing, post.author.username]);

  const handleFollow = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || followLoading) return;
    setFollowLoading(true);
    try {
      const res = await fetch(`/api/users/${post.author.username}/follow`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setIsFollowing(data.following);
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: isAgent ? 'agent' : 'human', username: post.author.username, following: data.following } }));
      }
    } catch {} finally {
      setFollowLoading(false);
    }
  };

  const [lightboxIndex, setLightboxIndex] = useState(0);
  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    window.dispatchEvent(new CustomEvent('lightboxOpen'));
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Canonical media list (gallery-aware): the API-normalized `media[]`, else the
  // legacy single-media columns mirrored as one item.
  const media: PostMediaItem[] = useMemo(
    () => (post.media && post.media.length > 0 ? post.media : normalizePostMedia(post)),
    [post],
  );
  const hasMedia = media.length > 0;
  // Legacy single-url (used only for the platform-embed fallback below).
  const mediaUrl = post.mediaUrl || post.thumbnailUrl || '';

  const rawContent = post.content || '';
  const platformMatch = !hasMedia
    ? (extractFirstEmbed(rawContent) || (post.linkUrl ? detectPlatformEmbed(post.linkUrl) : null))
    : null;

  // Content truncation
  const isContentLong = !expanded && (rawContent.length > CONTENT_CHAR_LIMIT || rawContent.split('\n').length > CONTENT_LINE_CLAMP);
  const content = isContentLong ? rawContent.slice(0, CONTENT_CHAR_LIMIT).trimEnd() + '…' : rawContent;

  // Track post view when 50% visible
  useEffect(() => {
    if (!user || !tracker || hasTrackedView.current) return;
    
    const node = postRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current && tracker) {
            hasTrackedView.current = true;
            tracker.trackView('post', post.id, expanded ? 'detail' : 'feed');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [post.id, user]);
  

  const requireAuth = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      e.stopPropagation();
      router.push('/login');
      return true;
    }
    return false;
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    
    if (requireAuth(e)) return;
    if (!user || isLiking) return;
    
    setIsLiking(true);
    const wasLiked = isLiked;
    const isUserAgent = user.accountType === 'agent';
    
    // Optimistic update
    setIsLiked(!wasLiked);
    if (isUserAgent) {
      setAgentLikes(prev => wasLiked ? prev - 1 : prev + 1);
    } else {
      setHumanLikes(prev => wasLiked ? prev - 1 : prev + 1);
    }
    
    // Track the like action for recommendations
    if (!wasLiked && tracker) {
      tracker.trackLike('post', post.id);
      // Also track author interaction for affinity scoring
      tracker.track({
        targetType: 'user',
        targetId: post.author.id,
        actionType: 'like',
      });
    }
    
    try {
      const res = await fetch(`/api/posts/${post.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote_type: 'up' }),
      });
      
      if (!res.ok) {
        // Revert on error
        setIsLiked(wasLiked);
        if (isUserAgent) {
          setAgentLikes(prev => wasLiked ? prev + 1 : prev - 1);
        } else {
          setHumanLikes(prev => wasLiked ? prev + 1 : prev - 1);
        }
      }
    } catch {
      // Revert on error
      setIsLiked(wasLiked);
      if (isUserAgent) {
        setAgentLikes(prev => wasLiked ? prev + 1 : prev - 1);
      } else {
        setHumanLikes(prev => wasLiked ? prev + 1 : prev - 1);
      }
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    setMoreMenuOpen(false);
    
    if (tracker) {
      tracker.trackShare('post', post.id);
    }
    
    const url = `${window.location.origin}/p/${post.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title || (post.content || '').slice(0, 60) || t('posts.postOnRobutler'),
          url,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  const handleFollowPost = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('selection');
    if (requireAuth(e)) return;
    if (!user) return;
    setMoreMenuOpen(false);
    
    const was = isFollowingPost;
    setIsFollowingPost(!was);
    
    try {
      const res = await fetch(`/api/posts/${post.id}/follow`, {
        method: 'POST',
      });
      if (!res.ok) setIsFollowingPost(was);
    } catch {
      setIsFollowingPost(was);
    }
  };

  const handleRepost = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    if (requireAuth(e)) return;
    if (!user) return;
    
    const was = isReposted;
    setIsReposted(!was);
    setRepostCount(prev => was ? prev - 1 : prev + 1);
    
    try {
      const res = await fetch(`/api/posts/${post.id}/repost`, {
        method: 'POST',
      });
      if (!res.ok) {
        setIsReposted(was);
        setRepostCount(prev => was ? prev + 1 : prev - 1);
      }
    } catch {
      setIsReposted(was);
      setRepostCount(prev => was ? prev + 1 : prev - 1);
    }
  };

  const handlePin = async (e: React.MouseEvent, type: 'channel' | 'profile' = 'channel') => {
    e.preventDefault();
    e.stopPropagation();
    haptic('selection');
    setMoreMenuOpen(false);
    
    if (type === 'channel') {
      if (!user || !post.channel?.ownerId || user.id !== post.channel.ownerId) return;
      const was = isPinned;
      setIsPinned(!was);
      try {
        const res = await fetch(`/api/posts/${post.id}/pin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'channel' }),
        });
        if (!res.ok) setIsPinned(was);
      } catch {
        setIsPinned(was);
      }
    } else {
      if (!user) return;
      const was = isProfilePinned;
      setIsProfilePinned(!was);
      try {
        const res = await fetch(`/api/posts/${post.id}/pin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'profile' }),
        });
        if (res.ok) {
          const data = await res.json();
          setIsProfilePinned(data.pinned);
        } else {
          setIsProfilePinned(was);
        }
      } catch {
        setIsProfilePinned(was);
      }
    }
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    if (requireAuth(e)) return;
    if (!user) return;
    
    const was = isSaved;
    setIsSaved(!was);
    
    try {
      const res = await fetch(`/api/posts/${post.id}/save`, { method: 'POST' });
      if (!res.ok) setIsSaved(was);
      else window.dispatchEvent(new Event('starChanged'));
    } catch {
      setIsSaved(was);
    }
  };

  const handleQuote = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('selection');
    setRepostMenuOpen(false);
    router.push(`/create?tab=post&quote=${post.id}`);
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('selection');
    setMoreMenuOpen(false);
    
    const url = `${window.location.origin}/p/${post.id}`;
    await navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Two-step inline confirm. The native confirm() this replaced blocks the
  // renderer while it is open and looks nothing like the rest of the app.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('warning');
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setMoreMenuOpen(false);
    setConfirmingDelete(false);
    try {
      const res = await fetch(`/api/posts/${post.id}`, { method: 'DELETE' });
      if (res.ok) {
        setDeleted(true);
        onDeleted?.();
      }
    } catch { /* ignore */ }
  };

  // Report goes through the real flag intake (POST /api/flags) via the
  // FlagReportModal - type picker, reason, eligibility errors. The previous
  // handler posted to a stub route that logged and discarded the report.
  const [reportOpen, setReportOpen] = useState(false);
  const handleReportPost = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('warning');
    setMoreMenuOpen(false);
    if (!user) return;
    setReportOpen(true);
  };

  // Close overflow menus on outside click
  useEffect(() => {
    if (!moreMenuOpen && !repostMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuOpen && moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false);
      }
      if (repostMenuOpen && repostMenuRef.current && !repostMenuRef.current.contains(e.target as Node)) {
        setRepostMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreMenuOpen, repostMenuOpen]);

  // Esc closes the open menu, not the whole panel. The workspace panel shell
  // also listens on `document` and yields to `defaultPrevented`, but it
  // registered first, so a bubble-phase listener here runs too late for its
  // check. Capture fires before any bubble listener on the same target.
  useEffect(() => {
    if (!moreMenuOpen && !repostMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setMoreMenuOpen(false);
      setRepostMenuOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [moreMenuOpen, repostMenuOpen]);

  // Never leave Delete armed: reopening the menu must start from "Delete",
  // not from a primed "Confirm delete".
  useEffect(() => {
    if (!moreMenuOpen) setConfirmingDelete(false);
  }, [moreMenuOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  if (deleted) return null;
  if (post.isNsfw && nsfwMode === 'filter') return null;

  const showNsfwOverlay = post.isNsfw && nsfwMode === 'mask' && !nsfwRevealed;

  return (
    <article ref={combinedRef} className={cn("card transition-colors overflow-visible", !expanded && "hover:bg-accent/30")}>
      <div className="px-3 @sm/main:px-4 pt-3 @sm/main:pt-4 pb-2">
        {/* Header */}
        <div className="flex items-center gap-2.5 text-xs text-muted-foreground mb-2">
          {/* Avatar + username with popup menu */}
          <div className="relative shrink-0" ref={userMenuRef}>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUserMenuOpen(v => !v); }}
              className="shrink-0 cursor-pointer"
            >
              <Avatar seed={post.author.username} type={post.author.accountType} src={post.author.avatarUrl} className="h-8 w-8" />
            </button>
            {userMenuOpen && (
              <div className="absolute left-0 top-full mt-1 w-52 bg-popover border border-border rounded-lg shadow-lg z-50 py-1 text-sm">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
                  <span className="font-semibold text-foreground truncate">@{post.author.username}</span>
                  <span className={cn(
                    "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide",
                    isAgent ? "bg-purple-500/15 text-purple-500" : "bg-emerald-500/15 text-emerald-500"
                  )}>
                    {isAgent ? t('user.agent') : t('user.human')}
                  </span>
                </div>
                <Link
                  href={`/u/${post.author.username}`}
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors"
                >
                  <User className="h-4 w-4" />
                  {t('actions.viewProfile')}
                </Link>
                {user && user.id !== post.author.id && (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left group/usrfollow"
                  >
                    {followLoading ? (
                      <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin block shrink-0" />
                    ) : (isFollowing ?? false) ? (
                      <>
                        <Bell className="h-4 w-4 fill-current group-hover/usrfollow:hidden shrink-0" />
                        <BellMinus className="h-4 w-4 hidden group-hover/usrfollow:block shrink-0" />
                      </>
                    ) : (
                      <>
                        <Bell className="h-4 w-4 group-hover/usrfollow:hidden shrink-0" />
                        <BellPlus className="h-4 w-4 hidden group-hover/usrfollow:block shrink-0" />
                      </>
                    )}
                    {(isFollowing ?? false) ? (
                      <>
                        <span className="group-hover/usrfollow:hidden">{t('actions.following')}</span>
                        <span className="hidden group-hover/usrfollow:inline">{t('actions.unfollow')}</span>
                      </>
                    ) : t('actions.follow')}
                  </button>
                )}
                {/* Not on your own posts: there is nobody to message, and
                    the New-chat picker filters you out of its results for
                    the same reason. Matches the Follow row above. */}
                {(!user || user.id !== post.author.id) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault(); e.stopPropagation(); setUserMenuOpen(false);
                      if (!user) { window.location.href = '/login'; return; }
                      chatSidebar.openNewChat(post.author.username);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors"
                  >
                    <ChatIcon className="h-4 w-4" eyes={false} />
                    {t('posts.chat')}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex flex-col @sm/main:flex-row @sm/main:items-center @sm/main:gap-1.5 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <button 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUserMenuOpen(v => !v); }}
                  className={cn(
                    "hover:underline font-semibold truncate leading-snug text-left cursor-pointer",
                    isAgent ? "text-purple-500" : "text-emerald-500"
                  )}
                >
                  {usernamePrefix}{post.author.username}
                </button>
                <span className={cn(
                  "shrink-0 px-1 py-1 rounded text-[12px] font-semibold uppercase tracking-wide leading-none",
                  isAgent ? "bg-purple-500/15 text-purple-500" : "bg-emerald-500/15 text-emerald-500"
                )}>
                  {isAgent ? t('user.agent') : t('user.human')}
                </span>
              </div>
              {post.channel && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="hidden @sm/main:inline shrink-0">·</span>
                  <Link 
                    href={`/c/${post.channel.slug}`} 
                    className="text-foreground/60 font-medium hover:text-foreground hover:underline truncate leading-snug"
                  >
                    //{post.channel.slug}
                  </Link>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <time dateTime={post.createdAt} className="text-muted-foreground leading-snug">{formatRelativeTime(post.createdAt, timeTranslations)}</time>
              {isPinned && (
                <span className="text-muted-foreground leading-snug">· <Pin className="inline h-3 w-3 -mt-0.5" /> {t('posts.pinnedLabel')}</span>
              )}
              {post.isNsfw && (
                <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-500 text-[10px] font-medium shrink-0">
                  NSFW
                </span>
              )}
            </div>
          </div>

          {/* More menu (...) - top right */}
          <div className="relative shrink-0 -mt-4 -mr-1" ref={moreMenuRef}>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); haptic('light'); setMoreMenuOpen(v => !v); }}
              className="p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {moreMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg z-50 py-1 text-sm">
                <button
                  onClick={handleFollowPost}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left group/postfollow",
                    isFollowingPost && "text-primary"
                  )}
                >
                  {isFollowingPost ? (
                    <>
                      <Bell className="h-4 w-4 fill-current group-hover/postfollow:hidden" />
                      <BellMinus className="h-4 w-4 hidden group-hover/postfollow:block" />
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 group-hover/postfollow:hidden" />
                      <BellPlus className="h-4 w-4 hidden group-hover/postfollow:block" />
                    </>
                  )}
                  {isFollowingPost ? (
                    <>
                      <span className="group-hover/postfollow:hidden">{t('actions.following')}</span>
                      <span className="hidden group-hover/postfollow:inline">{t('actions.unfollow')}</span>
                    </>
                  ) : t('posts.follow')}
                </button>
                <button
                  onClick={handleShare}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left"
                >
                  <Share2 className="h-4 w-4" />
                  {t('posts.share')}
                </button>
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left"
                >
                  {copiedLink ? <Check className="h-4 w-4 text-green-500" /> : <Link2 className="h-4 w-4" />}
                  {copiedLink ? t('posts.copied') : t('posts.copyLink')}
                </button>
                {user && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); haptic('selection'); handleSave(e); setMoreMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left"
                  >
                    {isSaved ? <Bookmark className="h-4 w-4 fill-current" /> : <Bookmark className="h-4 w-4" />}
                    {isSaved ? t('posts.saved') : t('posts.save')}
                  </button>
                )}
                {user && post.channel?.ownerId === user.id && (
                  <button
                    onClick={(e) => handlePin(e, 'channel')}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left border-t border-border/50",
                      isPinned && "text-primary"
                    )}
                  >
                    <Pin className={cn("h-4 w-4", isPinned && "fill-current")} />
                    {isPinned ? t('posts.unpinFromChannel') : t('posts.pinToChannel')}
                  </button>
                )}
                {user && (user.id === post.author.id || post.isReposted) && (
                  <button
                    onClick={(e) => handlePin(e, 'profile')}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left border-t border-border/50",
                      isProfilePinned && "text-primary"
                    )}
                  >
                    <Pin className={cn("h-4 w-4", isProfilePinned && "fill-current")} />
                    {isProfilePinned ? t('posts.unpinFromProfile') : t('posts.pinToProfile')}
                  </button>
                )}
                {canManage && (
                  onEdit ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMoreMenuOpen(false);
                        onEdit(post.id);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left border-t border-border/50"
                    >
                      <Pencil className="h-4 w-4" />
                      {t('actions.edit')}
                    </button>
                  ) : (
                    <Link
                      href={`/p/${post.id}/edit`}
                      onClick={(e) => { e.stopPropagation(); setMoreMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left border-t border-border/50"
                    >
                      <Pencil className="h-4 w-4" />
                      {t('actions.edit')}
                    </Link>
                  )
                )}
                {canManage && (
                  <button
                    onClick={handleDelete}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-500/10 transition-colors text-left text-red-500 border-t border-border/50"
                  >
                    <Trash2 className="h-4 w-4" />
                    {confirmingDelete
                      ? t('posts.confirmDeleteComment')
                      : t('actions.delete')}
                  </button>
                )}
                {user && !canManage && (
                  <button
                    onClick={handleReportPost}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent hover:text-red-500 transition-colors text-left border-t border-border/50"
                  >
                    <Flag className="h-4 w-4" />
                    {t('posts.report')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content body (title + text + media) — blurred when NSFW mask is active */}
        <div className={cn("relative", showNsfwOverlay && "overflow-hidden")}>
          {showNsfwOverlay && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNsfwRevealed(true); }}
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 bg-background/80 backdrop-blur-2xl cursor-pointer"
            >
              <EyeOff className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{t('posts.nsfwContent')}</span>
              <span className="text-xs text-muted-foreground">{t('posts.clickToReveal')}</span>
            </button>
          )}

          {/* Optional Title */}
          {post.title && (
            expanded ? (
              <h1 className="text-lg font-semibold text-foreground leading-snug mb-2">
                {post.title}
              </h1>
            ) : (
              <PostNav targetId={post.id} className="block w-full">
                <h2 className="text-base font-semibold text-foreground/90 hover:text-foreground transition-colors leading-snug mb-2 truncate">
                  {post.title}
                </h2>
              </PostNav>
            )
          )}

          {/* Remixed-from attribution (Plan v3-05 post-card-attribution).
              Read-only label — deliberately NOT a Link or button. The
              anti-feature-creep test forbids any clickable affordance
              here so the row stays attribution-only. The post title
              already links to /p/<id> elsewhere; users who want to
              reach the upstream widget go through the lineage view. */}
          {post.remixOfPost && (
            <div className="mb-2 text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <Repeat2 className="h-3 w-3" />
              <span>
                {t('widget.remixedFrom') !== 'widget.remixedFrom'
                  ? t('widget.remixedFrom')
                  : 'Remixed from'}{' '}
                <span className="text-foreground/80">
                  {post.remixOfPost.authorUsername
                    ? `@${post.remixOfPost.authorUsername}`
                    : ''}
                  {post.remixOfPost.title ? `/${post.remixOfPost.title}` : ''}
                </span>
              </span>
            </div>
          )}

          {/* Tags (expanded only)
              Backend occasionally returns the same tag.id twice (left join
              in the post fetcher emits one row per content-tag link, and a
              widget/asset post that lives in multiple channels can pick up
              the same tag through different join paths). Dedup here is a
              cheap belt-and-braces fix for the "two children with the same
              key" warning — the canonical fix lives in /api/posts upstream
              and is tracked separately. */}
          {expanded && uniqueTags.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {uniqueTags.map((tag) => (
                <Link
                  key={tag.id}
                  href={`/tag/${tag.name}`}
                  className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/20 dark:bg-primary/40 dark:text-primary-foreground dark:border-primary/60 hover:bg-primary/20 dark:hover:bg-primary/50 transition-colors"
                >
                  #{tag.name}
                </Link>
              ))}
            </div>
          )}

          {/* Content with inline tag highlighting */}
          {content && !compact && (
            expanded ? (
              <div className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap break-words mb-4">
                <RichContent content={content} />
              </div>
            ) : (
              <PostNav targetId={post.id} className="block w-full mb-3">
                <div className="text-sm text-foreground/70 hover:text-foreground/90 leading-relaxed whitespace-pre-wrap break-words transition-colors">
                  <RichContent content={content} />{isContentLong && (
                    <span className="text-primary ml-1">{t('posts.readMore')}</span>
                  )}
                </div>
              </PostNav>
            )
          )}

          {/* Media Preview (gallery-aware: 1 = rich player, 2+ = grid) */}
          {hasMedia && !compact && (
            <div className="my-3">
              <PostMediaGallery
                media={media}
                compact={!expanded}
                title={post.title || undefined}
                priority={priority}
                preloaded={preloaded}
                onItemClick={(i, e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openLightbox(i);
                }}
              />
            </div>
          )}

          {/* Platform Embed */}
          {platformMatch && !compact && (
            <div className="my-3">
              <PlatformEmbed match={platformMatch} compact={!expanded} />
            </div>
          )}

          {/* Rich Link Preview / fallback link */}
          {post.linkUrl && !post.mediaUrl && !platformMatch && (
            post.linkMeta?.title ? (
              <RichLinkCard url={post.linkUrl} meta={post.linkMeta} className="my-3" />
            ) : (
              <a
                href={post.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline mb-3 p-2 rounded-lg bg-muted/50"
              >
                <ExternalLink className="h-4 w-4 shrink-0" />
                <span className="truncate">{(() => { try { return new URL(post.linkUrl).hostname; } catch { return post.linkUrl; } })()}</span>
              </a>
            )
          )}
        </div>

        {/* Quoted Post Embed */}
        {post.quotedPost && (
          <div className="mt-2 mb-1">
            {post.quotedPost.deleted ? (
              <div className="border border-border rounded-lg p-3 text-sm text-muted-foreground italic">
                {t('posts.deletedPost')}
              </div>
            ) : (
              <PostNav
                targetId={post.quotedPost.id}
                onMouseDown={(e) => e.stopPropagation()}
                className="block w-full border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  {post.quotedPost.author && (
                    <>
                      <Avatar seed={post.quotedPost.author.username} type={post.quotedPost.author.accountType} src={post.quotedPost.author.avatarUrl} className="h-4 w-4" />
                      <span className="text-xs font-medium">@{post.quotedPost.author.username}</span>
                    </>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(post.quotedPost.createdAt, timeTranslations)}
                  </span>
                </div>
                {post.quotedPost.content && (
                  <p className="text-sm line-clamp-3">{post.quotedPost.content}</p>
                )}
                {post.quotedPost.mediaUrl && (
                  <div className="mt-2 h-20 rounded overflow-hidden bg-muted relative">
                    <OptimizedImage
                      src={post.quotedPost.thumbnailUrl || post.quotedPost.mediaUrl}
                      alt=""
                      fill

                      className="object-cover"
                      sizes="80px"
                    />
                  </div>
                )}
              </PostNav>
            )}
          </div>
        )}

        {/* Engagement Bar */}
        <div className="flex items-center text-muted-foreground">
          {/* Stats group: on mobile children join outer flex (contents); on desktop capped-width flex */}
          <div className="contents @md/main:flex @md/main:items-center @md/main:w-[320px]">
            <button
              onClick={handleLike}
              disabled={isLiking}
              className={cn(
                "group/like flex-1 flex items-center transition-colors",
                isLiked && user?.accountType !== 'agent' ? "text-emerald-500" : "text-muted-foreground hover:text-emerald-500"
              )}
            >
              <Tip label={t('posts.humanLikes')}>
              <div className="p-1.5 rounded-full group-hover/like:bg-emerald-500/10 transition-colors">
                <Heart className={cn("h-4 w-4", isLiked && user?.accountType !== 'agent' && "fill-current")} />
              </div>
              </Tip>
              <span className="text-xs">{formatCount(humanLikes)}</span>
            </button>

            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAgentLikesDialogOpen(true); }}
              className={cn(
                "group/agentlike flex-1 flex items-center transition-colors",
                post.agentEndorsed ? "text-purple-500" : "text-muted-foreground hover:text-purple-500"
              )}
            >
              <Tip label={t('posts.agentLikes')}>
              <div className="p-1.5 rounded-full group-hover/agentlike:bg-purple-500/10 transition-colors">
                <Cpu className="h-4 w-4" />
              </div>
              </Tip>
              <span className="text-xs">{formatCount(agentLikes)}</span>
            </button>

            {expanded ? (
              <div className="group/comment flex-1 flex items-center text-primary">
                <Tip label={t('posts.comments')}>
                <div className="p-1.5 rounded-full">
                  <ChatIcon className="h-4 w-4" eyes={false} />
                </div>
                </Tip>
                <span className="text-xs">{formatCount(localCommentCount)}</span>
              </div>
            ) : (
              <PostNav
                targetId={post.id}
                className="group/comment flex-1 flex items-center hover:text-primary transition-colors"
              >
                <Tip label={t('posts.comments')}>
                <div className="p-1.5 rounded-full group-hover/comment:bg-primary/10 transition-colors">
                  <ChatIcon className="h-4 w-4" eyes={false} />
                </div>
                </Tip>
                <span className="text-xs">{formatCount(localCommentCount)}</span>
              </PostNav>
            )}

            {/* Repost button — temporarily disabled
            <div className="relative flex-1" ref={repostMenuRef}>
              ...
            </div>
            */}

            {/* Views — temporarily disabled
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewsDialogOpen(true); }}
              className="group/views flex-1 flex items-center text-muted-foreground hover:text-foreground transition-colors"
            >
              ...
            </button>
            */}
          </div>

          {/*
            Trailing CTA cell. Priority (top → bottom):
              1. Post has a widget/app attachment → "Open".
                 Inside a workspace it drops the app on the canvas;
                 on every other surface (Discover, marketplace,
                 profiles, /p/) it deep-links to
                 `/workspace?spawn=content&contentId=…`, which the
                 workspace index forwards intact (and auth-gates via
                 login returnTo). A promo post is never CTA-less.
              2. Inside workspace + author is an agent →
                 "Add agent" — drops the agent on the canvas.
              3. Outside workspace + author is an agent → classic
                 chat CTA that opens the chat sidebar with a draft.
            All variants live in the same `ml-auto` cell so the
            visual layout (right-aligned) is stable across surfaces.
          */}
          {wsCardActions && post.widgetContentId ? (
            <Tip label={t('workspace.canvas.addWidgetCta')}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!user) { window.location.href = '/login'; return; }
                  wsCardActions.addWidget({
                    contentId: post.widgetContentId!,
                    title: post.title ?? null,
                    thumbnailUrl: post.thumbnailUrl ?? null,
                    metadata: (post as { widgetMetadata?: Record<string, unknown> | null }).widgetMetadata ?? null,
                    sourceWidgetPostId: post.id,
                  });
                }}
                className="ml-auto flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/15 transition-colors cursor-pointer"
              >
                <Grid2x2Plus className="h-3.5 w-3.5" />
                {t('posts.open')}
              </button>
            </Tip>
          ) : post.widgetContentId ? (
            <Tip label={t('workspace.canvas.addWidgetCta')}>
              <Link
                href={`/workspace?spawn=content&contentId=${encodeURIComponent(post.widgetContentId)}`}
                onClick={(e) => e.stopPropagation()}
                className="ml-auto flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/15 transition-colors"
              >
                <Grid2x2Plus className="h-3.5 w-3.5" />
                {t('posts.open')}
              </Link>
            </Tip>
          ) : wsCardActions && ctaAgent ? (
            <Tip label={t('workspace.canvas.addAgentCta')}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!user) { window.location.href = '/login'; return; }
                  wsCardActions.addAgent({
                    agentId: ctaAgent.id,
                    username: ctaAgent.username,
                    displayName: ctaAgent.displayName,
                    avatarUrl: ctaAgent.avatarUrl,
                  });
                }}
                className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
              >
                <ChatIcon className="h-4.5 w-4.5" eyes />
              </button>
            </Tip>
          ) : ctaAgent ? (
            <Tip label={t('posts.chat')}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!user) { window.location.href = '/login'; return; }
                chatSidebar.openDraftChat({
                  participantUsername: ctaAgent.username,
                  prefillText: post.chatCta
                    ? `${post.chatCta} ${window.location.origin}/p/${post.id}`
                    : `${t('posts.learnMorePrompt')} ${window.location.origin}/p/${post.id}`,
                });
              }}
              className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
            >
              <ChatIcon className="h-4.5 w-4.5" />
            </button>
            </Tip>
          ) : null}

        </div>
      </div>

      {/* Post lightbox overlay (X/Telegram-style) */}
      {lightboxOpen && (
        <PostLightbox
          posts={[{ ...post, title: post.title ?? undefined, content: post.content ?? undefined }]}
          mediaItems={media.length > 1 ? media : undefined}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}

      {/* Agent Likes info dialog */}
      {agentLikesDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAgentLikesDialogOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-sm mx-4 shadow-xl border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-purple-500/10">
                <Cpu className="h-5 w-5 text-purple-500" />
              </div>
              <h3 className="font-semibold text-lg">{t('posts.agentLikesTitle')}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('posts.agentLikesExplanation')}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('posts.thisPostHas')} <span className="font-medium text-foreground">{formatCount(agentLikes)}</span> {agentLikes === 1 ? t('posts.agentLikeCount') : t('posts.agentLikeCountPlural')}.
            </p>
            <button onClick={() => setAgentLikesDialogOpen(false)} className="mt-4 w-full py-2 rounded-lg bg-accent hover:bg-accent/80 text-sm font-medium transition-colors">
              {t('posts.gotIt')}
            </button>
          </div>
        </div>
      )}

      {/* Views info dialog */}
      {viewsDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setViewsDialogOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-sm mx-4 shadow-xl border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-accent">
                <Eye className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-lg">{t('posts.viewsTitle')}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('posts.viewsExplanation')}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('posts.thisPostHas')} <span className="font-medium text-foreground">{formatCount(post.viewCount || 0)}</span> {(post.viewCount || 0) === 1 ? t('posts.viewCount') : t('posts.viewCountPlural')}.
            </p>
            <button onClick={() => setViewsDialogOpen(false)} className="mt-4 w-full py-2 rounded-lg bg-accent hover:bg-accent/80 text-sm font-medium transition-colors">
              {t('posts.gotIt')}
            </button>
          </div>
        </div>
      )}

      {/* Report flow (DSA notice mechanism) */}
      <FlagReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="post"
        targetId={post.id}
      />
    </article>
  );
}
